import { Link } from 'react-router-dom';
import Seo, { SITE_NAME, SITE_ORIGIN } from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { faqs } from '@/mocks/faqs';
import { testimonials } from '@/mocks/testimonials';
import { getCategories, buildCategoryPath } from '@/utils/articles';

const principles = [
  { icon: 'ri-shield-check-line', title: '区分事实与传闻', text: '凡是能核实的公开信息，我们会标注来源；仍处于讨论阶段的内容，会明确写成"尚在传闻"，不混为一谈。' },
  { icon: 'ri-user-heart-line', title: '保护无关的人', text: '处理投稿与匿名爆料时，我们会隐去与事件无关的个人信息，避免话题伤害到不相关的人。' },
  { icon: 'ri-scales-3-line', title: '给各方回应的机会', text: '涉及具体当事方的爆料，我们尽力呈现多方说法，而不是只保留最劲爆的那一面。' },
  { icon: 'ri-refresh-line', title: '持续更正与追更', text: '事件有进展就补更，信息有出入就更正。我们宁可慢一点，也不愿意传错。' },
];

const stats = [
  { value: '1,200+', label: '累计更新篇数' },
  { value: '4', label: '内容栏目' },
  { value: '每日 2 次', label: '集中更新时段' },
  { value: '2019', label: '内容团队成立' },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${SITE_NAME}`,
    url: `${SITE_ORIGIN}/about`,
    description: '18爆料网的品牌定位、内容原则与联系方式。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: `联系我们 - ${SITE_NAME}`,
    url: `${SITE_ORIGIN}/about`,
    description: '投稿、更正与合作请联系 contact@18baoliao.com。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  },
];

export default function AboutPage() {
  const categories = getCategories();

  return (
    <div className="w-full">
      <Seo
        title="关于我们 | 18爆料网的内容原则与联系方式"
        description="了解18爆料网的品牌定位、内容原则与团队理念。我们整理公开信息与网友投稿，坚持区分事实与传闻，保护无关当事人，并欢迎投稿与内容更正。"
        keywords="18爆料网,关于18爆料网,18爆料网内容原则,娱乐资讯投稿,18爆料网联系方式"
        path="/about"
        schema={schema}
      />

      <section className="w-full bg-foreground-950 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="[&_a]:text-background-300 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>
          <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">关于 18爆料网</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
            我们是一个专注娱乐爆料资讯的中文内容站。名字里的"18"不是噱头，而是一种态度——对每一条信息都多想一层，
            对每一个当事人都不轻易下结论。每天都有新瓜，但我们更希望你能吃得明白。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="WHO WE ARE" title="我们是谁" />
            <div className="flex flex-col gap-4 text-sm leading-7 text-foreground-700 md:text-[15px]">
              <p>
                18爆料网由一支小型内容团队在 2019 年创立。最初只是几个喜欢追娱乐新闻的编辑，把自己每天看到的信息整理成一句话摘要，
                发在一个小小的账号里。后来留言的人越来越多，有人开始主动提供线索，这才慢慢有了今天的样子。
              </p>
              <p>
                现在的我们，每天固定更新两次，覆盖明星动态、网红争议、素人投稿与圈内观察四个方向。内容来源主要有三部分：
                公开可查的媒体报道与官方声明、网友主动投稿的亲身经历、以及从业者的匿名分享。
              </p>
              <p>
                我们不生产"独家猛料"，也不靠标题党吸引点击。对我们来说，把一件事讲清楚、讲准确，比抢第一时间更重要。
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-secondary-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              一个"看得懂、看得完"的娱乐资讯站。我们用清晰的栏目分类、克制的标题和明确的信息分级，
              帮你在铺天盖地的消息里，快速找到值得了解的那几条。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-xl bg-background-50 p-4">
                  <p className="font-heading text-xl font-bold text-primary-600">{item.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 py-10 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            eyebrow="HOW WE WORK"
            title="我们的内容原则"
            description="这四条不是写在墙上的口号，而是编辑每天真正拿来做判断的依据。"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xl text-primary-600">
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="READER VOICES"
          title="读者怎么说"
          description="我们很少做推广，来的人大多是被内容留住的。这些是他们留下的原话。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((item) => (
            <figure key={item.name} className="flex h-full flex-col rounded-xl border border-background-200 bg-background-50 p-5">
              <i className="ri-double-quotes-l text-2xl text-accent-400" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-background-100 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100 py-10 md:py-16">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHeading eyebrow="FAQ" title="投稿与常见疑问" />
            <div className="rounded-xl border border-primary-100 bg-primary-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">想投稿或反馈内容？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                欢迎把线索、亲身经历或更正意见发给我们。请注明来意与联系方式，方便我们二次确认。
              </p>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-send-line text-base text-primary-600" />
                  投稿与线索：contact@18baoliao.com
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-base text-primary-600" />
                  内容更正：correct@18baoliao.com
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-hand-heart-line text-base text-primary-600" />
                  合作事宜：hi@18baoliao.com
                </li>
              </ul>
            </div>
            <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">浏览各栏目</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={buildCategoryPath(category.slug)}
                    className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>
    </div>
  );
}